import { brandColor } from '../../../../_helpers/brand-color';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarModule,
  CalendarView,
} from 'angular-calendar';
import moment from 'moment';
import { Subject } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService } from 'primeng/dynamicdialog';
import { FormsModule } from '@angular/forms';
import { PopoverModule } from 'primeng/popover';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import IAppointment from '../../../../_interfaces/IAppointment';
import ICalendar from '../../../../_interfaces/ICalendar';
import IPatient from '../../../../_interfaces/IPatients';
import IPatientHasTreatment from '../../../../_interfaces/IPatientHasTreatment';
import { ICalendarSettings } from '../../../../_interfaces/ICalendarSettings';
import { IAppointmentRejectRequestPayload } from '../../../../_interfaces/IAppointmentRequest';
import { BaseComponent } from '../../../base/base.component';
import { FormCalendarNoteComponent } from '../../../forms/form-calendar-note/form-calendar-note.component';
import { RejectAppointmentRequestDialogComponent } from '../../../appointment/reject-appointment-request-dialog/reject-appointment-request-dialog.component';
import { SessionService } from '../../../../_services/session/session.service';
import { AppointmentService } from '../../../../_services/appointments/appointment.service';
import { CalendarTimeBlocksService } from '../../../../_services/calendars/calendar-time-blocks.service';
import { CalendarAppointmentsMobileFormComponent } from './calendar-appointments-mobile-form.component';
import {
  MobileAppointmentSelection,
  MobileCalendarAction,
  MobileCalendarEvent,
} from './calendar-appointments-mobile.models';

@Component({
  standalone: true,
  selector: 'app-calendar-appointments-view',
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    ButtonModule,
    DatePickerModule,
    PopoverModule,
    TranslatePipe,
    RejectAppointmentRequestDialogComponent,
    CalendarAppointmentsMobileFormComponent,
  ],
  templateUrl: './calendar-appointments-view.component.html',
  styleUrl: './calendar-appointments-view.component.scss',
})
export class CalendarAppointmentsViewComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  private static readonly TAP_DRAG_THRESHOLD_PX = 8;
  private static readonly SELECTION_EVENT_ID = '__mobile-selection__';

  @ViewChild('pendingRequestsPopover') pendingRequestsPopover: any;

  @Input() calendars: ICalendar[] = [];
  @Input() events: CalendarEvent[] = [];
  @Input() locale = 'es';
  @Input() viewDate: Date = new Date();
  @Input() view: CalendarView = CalendarView.Week;
  @Input() refresh: Subject<void> = new Subject<void>();
  @Input() dayStartHour = 8;
  @Input() dayEndHour = 20;
  @Input() precision = 30;
  @Input() settings!: ICalendarSettings;
  @Input() openSettings?: (() => void) | undefined;
  @Input() onAction: (config: MobileCalendarAction) => void = () => undefined;
  @Input() patient?: IPatient;
  @Input() treatments?: IPatientHasTreatment;
  @Input() quickMode = false;

  @Output() eventTimesChanged = new EventEmitter<CalendarEventTimesChangedEvent>();

  public CalendarView = CalendarView;
  public moment = moment;
  public selectedMobileDate = new Date();
  public drawerVisible = false;
  public currentSelection: MobileAppointmentSelection | null = null;
  public editingAppointment: IAppointment | null = null;
  public editingCalendarEvent: CalendarEvent | null = null;
  public mobileEvents: MobileCalendarEvent[] = [];
  public pendingRequestsCount = 0;
  public pendingRequestsLoading = false;
  public pendingRequests: IAppointment[] = [];
  public rejectDialogVisible = false;
  public rejectRequestWorking = false;
  public rejectTarget: IAppointment | null = null;
  public selectionChoiceVisible = false;
  public timeBlockFormVisible = false;
  public selectedTimeBlockCalendarIds: number[] = [];
  public timeBlockReason = '';
  public timeBlockNotes = '';
  public savingTimeBlock = false;

  private pointerState:
    | {
        pointerId: number;
        startDate: Date;
        startY: number;
        segmentHeight: number;
        armed: boolean;
        dragging: boolean;
        moved: boolean;
        target: HTMLElement | null;
        activationTimeoutId: ReturnType<typeof setTimeout> | null;
      }
    | null = null;
  private eventPointerState:
    | {
        pointerId: number;
        startX: number;
        startY: number;
        event: CalendarEvent;
      }
    | null = null;
  private suppressNextSegmentClick = false;

  constructor(
    public session: SessionService,
    private readonly translate: TranslateService,
    private readonly dialogService: DialogService,
    private readonly appointmentService: AppointmentService,
    private readonly calendarTimeBlocksService: CalendarTimeBlocksService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.selectedMobileDate = this.viewDate;
    this.loadPendingRequestsCount();
    this.rebuildMobileEvents();
    if (this.view === CalendarView.Month) {
      this.setView(CalendarView.Week);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewDate']?.currentValue) {
      this.selectedMobileDate = changes['viewDate'].currentValue;
    }
    if (changes['view']?.currentValue === CalendarView.Month) {
      this.view = CalendarView.Week;
    }
    if (changes['events'] || changes['view'] || changes['viewDate']) {
      this.rebuildMobileEvents();
    }
  }

  override ngOnDestroy(): void {
    this.clearPointerState();
    super.ngOnDestroy();
  }

  get isWeekView(): boolean {
    return this.view === CalendarView.Week;
  }

  get isDayView(): boolean {
    return this.view === CalendarView.Day;
  }

  get daysInView(): number {
    return this.isDayView ? 1 : 7;
  }

  get mobileTitle(): string {
    moment.locale(this.translate.currentLang || 'es');
    if (this.isDayView) {
      return moment(this.viewDate).format('dddd, D MMM');
    }
    return moment(this.viewDate).format('MMMM YYYY');
  }

  get calendarViewDate(): Date {
    if (this.isDayView) {
      return this.viewDate;
    }

    return moment(this.viewDate).startOf('isoWeek').toDate();
  }

  public getWeekdayLabel(date: Date): string {
    const weekday = moment(date).isoWeekday();
    const lang = (this.translate.currentLang || this.locale || 'es').toLowerCase();

    if (lang.startsWith('es')) {
      return ['L', 'M', 'X', 'J', 'V', 'S', 'D'][weekday - 1];
    }

    if (lang.startsWith('ca')) {
      return ['DL', 'DT', 'DC', 'DJ', 'DV', 'DS', 'DG'][weekday - 1];
    }

    return moment(date).locale(lang).format('dd').charAt(0).toUpperCase();
  }

  public setView(view: CalendarView): void {
    const nextView = view === CalendarView.Day ? CalendarView.Day : CalendarView.Week;
    this.view = nextView;
    this.rebuildMobileEvents();
    this.onAction({
      date: this.viewDate,
      view: nextView,
      retrieve: { appointments: true, notes: true },
    });
  }

  public navigatePrevious(): void {
    this.onAction({
      date: moment(this.viewDate)
        .subtract(this.isDayView ? 1 : 7, 'days')
        .toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  public navigateNext(): void {
    this.onAction({
      date: moment(this.viewDate)
        .add(this.isDayView ? 1 : 7, 'days')
        .toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  public goToToday(): void {
    this.goToDate(moment().toDate());
  }

  public goToDate(date: Date): void {
    this.selectedMobileDate = date;
    this.currentSelection = null;
    this.rebuildMobileEvents();
    this.onAction({
      date,
      retrieve: { appointments: true, notes: true },
    });
  }

  public onMobileDateSelect(date: Date): void {
    this.goToDate(date);
  }

  public onHeaderDaySelected(date: Date): void {
    if (this.isWeekView) {
      this.setView(CalendarView.Day);
    }
    this.goToDate(date);
  }

  public onCalendarEventClicked(event: CalendarEvent): void {
    if ((event as MobileCalendarEvent)?.meta?.mobileSelection) {
      return;
    }
    const appointment =
      (event as MobileCalendarEvent)?.appointment ||
      (event as any)?.meta?.appointment ||
      null;
    if (appointment) {
      this.editingAppointment = {
        ...appointment,
        name: appointment.name || event.title || '',
        start: appointment.start || event.start,
        end: appointment.end || event.end,
      };
      this.editingCalendarEvent = event;
      this.currentSelection = null;
      this.drawerVisible = true;
      return;
    }
  }

  public onMobileEventPointerDown(event: PointerEvent, calendarEvent: CalendarEvent): void {
    this.eventPointerState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      event: calendarEvent,
    };
  }

  public onMobileEventPointerUp(event: PointerEvent, calendarEvent: CalendarEvent): void {
    const state = this.eventPointerState;
    this.eventPointerState = null;
    if (!state || state.pointerId !== event.pointerId) {
      return;
    }

    const moved = Math.hypot(
      event.clientX - state.startX,
      event.clientY - state.startY,
    );

    if (moved > CalendarAppointmentsViewComponent.TAP_DRAG_THRESHOLD_PX) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.onCalendarEventClicked(calendarEvent || state.event);
  }

  public onMobileEventPointerCancel(event: PointerEvent): void {
    if (this.eventPointerState?.pointerId === event.pointerId) {
      this.eventPointerState = null;
    }
  }

  public suppressMobileEventClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onMobileEventTimesChanged(change: CalendarEventTimesChangedEvent): void {
    this.eventTimesChanged.emit(change);
  }

  public onSegmentPointerDown(
    event: PointerEvent,
    segmentDate: Date,
    segmentHeight: number,
  ): void {
    if (
      (!this.hasPermission('APPOINTMENTS.CREATE') &&
        !this.hasPermission('CALENDAR_TIME_BLOCKS.CREATE')) ||
      !this.calendars?.length
    ) {
      return;
    }

    const target = event.currentTarget as HTMLElement | null;

    this.pointerState = {
      pointerId: event.pointerId,
      startDate: new Date(segmentDate),
      startY: event.clientY,
      segmentHeight: segmentHeight || 30,
      armed: event.pointerType === 'mouse' || event.pointerType === 'pen',
      dragging: false,
      moved: false,
      target,
      activationTimeoutId: null,
    };

    if (event.pointerType === 'touch') {
      this.pointerState.activationTimeoutId = setTimeout(() => {
        if (
          this.pointerState &&
          this.pointerState.pointerId === event.pointerId &&
          !this.pointerState.moved
        ) {
          this.armPointerSelection(event.pointerId);
        }
      }, 180);
    } else {
      this.armPointerSelection(event.pointerId);
    }
  }

  public onSegmentClick(event: MouseEvent, segmentDate: Date): void {
    if (this.suppressNextSegmentClick) {
      this.suppressNextSegmentClick = false;
      return;
    }

    if (
      (!this.hasPermission('APPOINTMENTS.CREATE') &&
        !this.hasPermission('CALENDAR_TIME_BLOCKS.CREATE')) ||
      !this.calendars?.length
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openSelectionFromDates(
      new Date(segmentDate),
      this.getDefaultSelectionEnd(segmentDate),
      'tap',
    );
  }

  @HostListener('document:pointermove', ['$event'])
  public onDocumentPointerMove(event: PointerEvent): void {
    if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) {
      return;
    }

    const deltaY = event.clientY - this.pointerState.startY;
    const absDeltaY = Math.abs(deltaY);

    if (!this.pointerState.armed) {
      if (absDeltaY > CalendarAppointmentsViewComponent.TAP_DRAG_THRESHOLD_PX) {
        this.pointerState.moved = true;
      }
      return;
    }

    const hasDragged =
      deltaY > CalendarAppointmentsViewComponent.TAP_DRAG_THRESHOLD_PX;

    if (!hasDragged) {
      return;
    }

    event.preventDefault();
    const slotCount = Math.max(
      1,
      Math.ceil(deltaY / Math.max(this.pointerState.segmentHeight, 1)),
    );
    this.pointerState.dragging = true;

    const durationMinutes = Math.max(
      this.getDefaultSelectionDuration(),
      slotCount * this.precision,
    );
    const start = this.pointerState.startDate;
    const end = this.clampSelectionEnd(
      moment(start).add(durationMinutes, 'minutes').toDate(),
      start,
    );

    this.currentSelection = this.buildSelection(
      start,
      end,
      'drag',
    );
    this.rebuildMobileEvents();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  public onDocumentPointerUp(event: PointerEvent): void {
    if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) {
      return;
    }

    if (this.pointerState.activationTimeoutId) {
      clearTimeout(this.pointerState.activationTimeoutId);
    }

    this.safeReleasePointerCapture(this.pointerState.target, event.pointerId);
    const shouldOpenTapSelection =
      !this.pointerState.dragging && !this.pointerState.moved;
    const selection = this.pointerState.dragging
      ? this.currentSelection ||
        this.buildSelection(
          this.pointerState.startDate,
          this.getDefaultSelectionEnd(this.pointerState.startDate),
          'drag',
        )
      : shouldOpenTapSelection
        ? this.buildSelection(
            this.pointerState.startDate,
            this.getDefaultSelectionEnd(this.pointerState.startDate),
            'tap',
          )
        : null;

    this.clearPointerState();
    this.suppressNextSegmentClick = !!selection;
    setTimeout(() => {
      this.suppressNextSegmentClick = false;
    }, 0);
    if (selection) {
      this.openSelection(selection);
    }
  }

  @HostListener('window:blur')
  @HostListener('document:visibilitychange')
  public clearPointerState(): void {
    if (this.pointerState?.activationTimeoutId) {
      clearTimeout(this.pointerState.activationTimeoutId);
    }
    this.pointerState = null;
  }

  private armPointerSelection(pointerId: number): void {
    if (!this.pointerState || this.pointerState.pointerId !== pointerId) {
      return;
    }

    this.pointerState.armed = true;
    this.safeSetPointerCapture(this.pointerState.target, pointerId);
  }

  private openSelectionFromDates(
    start: Date,
    end: Date,
    source: 'tap' | 'drag',
  ): void {
    this.openSelection(this.buildSelection(start, end, source));
  }

  private openSelection(selection: MobileAppointmentSelection): void {
    this.editingAppointment = null;
    this.editingCalendarEvent = null;
    this.currentSelection = selection;
    this.drawerVisible = false;
    this.selectionChoiceVisible = true;
    this.timeBlockFormVisible = false;
    this.selectedTimeBlockCalendarIds = this.calendars
      .map((calendar) => Number(calendar.id))
      .filter(Boolean);
    this.timeBlockReason = '';
    this.timeBlockNotes = '';
    this.rebuildMobileEvents();
  }

  private safeSetPointerCapture(target: HTMLElement | null, pointerId: number): void {
    try {
      target?.setPointerCapture?.(pointerId);
    } catch {
      return;
    }
  }

  private safeReleasePointerCapture(target: HTMLElement | null, pointerId: number): void {
    try {
      target?.releasePointerCapture?.(pointerId);
    } catch {
      return;
    }
  }

  public onDrawerSaved(): void {
    this.currentSelection = null;
    this.selectionChoiceVisible = false;
    this.timeBlockFormVisible = false;
    this.editingAppointment = null;
    this.editingCalendarEvent = null;
    this.drawerVisible = false;
    this.rebuildMobileEvents();
    this.onAction({
      retrieve: { appointments: true, notes: false },
    });
  }

  public onDrawerVisibilityChange(visible: boolean): void {
    this.drawerVisible = visible;
    if (!visible) {
      this.currentSelection = null;
      this.selectionChoiceVisible = false;
      this.timeBlockFormVisible = false;
      this.editingAppointment = null;
      this.editingCalendarEvent = null;
      this.rebuildMobileEvents();
    }
  }

  public openAppointmentFromSelection(): void {
    if (!this.currentSelection || !this.hasPermission('APPOINTMENTS.CREATE')) {
      return;
    }
    this.selectionChoiceVisible = false;
    this.timeBlockFormVisible = false;
    this.drawerVisible = true;
    this.rebuildMobileEvents();
  }

  public openTimeBlockFormFromSelection(): void {
    if (!this.currentSelection || !this.hasPermission('CALENDAR_TIME_BLOCKS.CREATE')) {
      return;
    }
    this.selectionChoiceVisible = false;
    this.timeBlockFormVisible = true;
    this.rebuildMobileEvents();
  }

  public cancelSelectionChoice(): void {
    this.selectionChoiceVisible = false;
    this.timeBlockFormVisible = false;
    this.currentSelection = null;
    this.timeBlockReason = '';
    this.timeBlockNotes = '';
    this.rebuildMobileEvents();
  }

  public toggleTimeBlockCalendar(calendar: ICalendar, checked: boolean): void {
    const calendarId = Number(calendar.id);
    if (!calendarId) return;
    if (checked) {
      this.selectedTimeBlockCalendarIds = [
        ...new Set([...this.selectedTimeBlockCalendarIds, calendarId]),
      ];
      return;
    }
    this.selectedTimeBlockCalendarIds = this.selectedTimeBlockCalendarIds.filter(
      (id) => id !== calendarId,
    );
  }

  public isTimeBlockCalendarChecked(calendar: ICalendar): boolean {
    return this.selectedTimeBlockCalendarIds.includes(Number(calendar.id));
  }

  public createTimeBlockFromSelection(): void {
    if (
      !this.currentSelection ||
      !this.selectedTimeBlockCalendarIds.length ||
      this.savingTimeBlock ||
      !this.hasPermission('CALENDAR_TIME_BLOCKS.CREATE')
    ) {
      return;
    }

    this.savingTimeBlock = true;
    this.calendarTimeBlocksService
      .createBulk(this.customers_id, this.site, {
        calendars_ids: this.selectedTimeBlockCalendarIds,
        start: this.currentSelection.start,
        end: this.currentSelection.end,
        reason: this.timeBlockReason,
        notes: this.timeBlockNotes,
      })
      .subscribe({
        next: () => {
          this.savingTimeBlock = false;
          this.timeBlockFormVisible = false;
          this.cancelSelectionChoice();
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
        error: () => {
          this.savingTimeBlock = false;
        },
      });
  }

  private getDefaultSelectionDuration(): number {
    const roundedDuration =
      Math.ceil(30 / Math.max(this.precision, 1)) * Math.max(this.precision, 1);
    return Math.max(this.precision, roundedDuration);
  }

  private getSelectionCalendar(): ICalendar {
    return this.calendars[0];
  }

  private getDefaultSelectionEnd(start: Date): Date {
    const draftEnd = moment(start)
      .add(this.getDefaultSelectionDuration(), 'minutes')
      .toDate();
    return this.clampSelectionEnd(draftEnd, start);
  }

  private clampSelectionEnd(end: Date, start: Date): Date {
    const latestAllowed =
      this.dayEndHour >= 24
        ? moment(start).endOf('day')
        : moment(start)
            .startOf('day')
            .hour(this.dayEndHour)
            .minute(0)
            .second(0);

    if (moment(end).isAfter(latestAllowed)) {
      return latestAllowed.toDate();
    }

    if (moment(end).isSameOrBefore(start)) {
      return moment(start).add(Math.max(this.precision, 1), 'minutes').toDate();
    }

    return end;
  }

  private buildSelection(
    start: Date,
    end: Date,
    source: 'tap' | 'drag',
  ): MobileAppointmentSelection {
    return {
      start: new Date(start),
      end: new Date(end),
      calendar: this.getSelectionCalendar(),
      source,
    };
  }

  private rebuildMobileEvents(): void {
    const baseEvents = (this.events || []).map((event) => {
      const appointment =
        (event as MobileCalendarEvent).appointment ||
        (event as MobileCalendarEvent).meta?.appointment ||
        null;

      return {
        ...event,
        appointment: appointment || undefined,
        meta: {
          ...(event.meta || {}),
          ...(appointment ? { appointment } : {}),
        },
        draggable: !!appointment && event.draggable === true,
        resizable: {
          beforeStart: false,
          afterEnd: false,
        },
      };
    }) as MobileCalendarEvent[];

    if (
      !this.currentSelection ||
      this.drawerVisible ||
      this.currentSelection.source !== 'drag'
    ) {
      this.mobileEvents = baseEvents;
      return;
    }

    this.mobileEvents = [
      ...baseEvents,
      {
        id: CalendarAppointmentsViewComponent.SELECTION_EVENT_ID,
        title: this.translate.instant('APPOINTMENT.MOBILE.SELECTION_LABEL'),
        start: this.currentSelection.start,
        end: this.currentSelection.end,
        color: {
          primary: brandColor(),
          secondary: brandColor(500, 0.18),
        },
        cssClass: 'vc-mobile-calendar__selection-event',
        meta: {
          mobileSelection: true,
        },
        draggable: false,
        resizable: {
          beforeStart: false,
          afterEnd: false,
        },
      },
    ];
  }

  public addNoteMobile(): void {
    const ref = this.dialogService.open(FormCalendarNoteComponent, {
      width: '95%',
      closable: true,
      modal: true,
      data: {
        calendars: this.calendars,
        date: this.viewDate,
      },
      inputValues: {
        onUpdate: () => {
          ref.close();
          this.onAction({
            retrieve: { appointments: false, notes: true },
          });
        },
      },
      header: this.translate.instant('CALENDAR.NOTES.ADD'),
    });
  }

  private isExpiredPortalPendingRequest(
    appointment?: IAppointment | null,
    now: Date = new Date(),
  ): boolean {
    if (!appointment) return false;
    if (appointment.source !== 'PATIENT_PORTAL') return false;
    if (appointment.approval_state !== 'PENDING_APPROVAL') return false;
    if (!appointment.end) return false;
    return new Date(appointment.end).getTime() < now.getTime();
  }

  private filterVisiblePendingRequests(
    appointments: IAppointment[] | null | undefined,
  ): IAppointment[] {
    const now = new Date();
    return (appointments || []).filter(
      (appointment) => !this.isExpiredPortalPendingRequest(appointment, now),
    );
  }

  public loadPendingRequests(): void {
    if (!this.customers_id || !this.site?.id) {
      this.pendingRequests = [];
      this.pendingRequestsCount = 0;
      return;
    }

    this.pendingRequestsLoading = true;
    this.appointmentService
      .getPatientRequests(this.customers_id, this.site, 'pending')
      .subscribe({
        next: (requests) => {
          this.pendingRequestsLoading = false;
          this.pendingRequests = this.filterVisiblePendingRequests(requests);
          this.pendingRequestsCount = this.pendingRequests.length;
        },
        error: () => {
          this.pendingRequestsLoading = false;
          this.pendingRequests = [];
          this.pendingRequestsCount = 0;
        },
      });
  }

  public loadPendingRequestsCount(): void {
    if (!this.customers_id || !this.site?.id) {
      this.pendingRequestsCount = 0;
      return;
    }

    this.appointmentService
      .getPatientRequestsCount(this.customers_id, this.site)
      .subscribe({
        next: (response) => {
          this.pendingRequestsCount = Number(response?.pending || 0);
        },
        error: () => {
          this.pendingRequestsCount = 0;
        },
      });
  }

  public approveRequest(appointment: IAppointment): void {
    if (!appointment?.id || !this.customers_id || !this.site?.id) return;
    this.appointmentService
      .approvePatientRequest(this.customers_id, this.site, appointment.id)
      .subscribe({
        next: () => {
          this.loadPendingRequests();
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
      });
  }

  public rejectRequest(appointment: IAppointment): void {
    if (!appointment?.id) return;
    this.rejectTarget = appointment;
    this.pendingRequestsPopover?.hide?.();
    this.rejectDialogVisible = true;
  }

  public confirmRejectRequest(payload: IAppointmentRejectRequestPayload): void {
    if (
      !this.rejectTarget?.id ||
      !this.customers_id ||
      !this.site?.id ||
      this.rejectRequestWorking
    ) {
      return;
    }

    this.rejectRequestWorking = true;
    this.appointmentService
      .rejectPatientRequest(this.customers_id, this.site, this.rejectTarget.id, {
        reason: payload?.reason,
        counter_proposal: payload?.counter_proposal,
      })
      .subscribe({
        next: () => {
          this.rejectRequestWorking = false;
          this.closeRejectDialog();
          this.loadPendingRequests();
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
        error: () => {
          this.rejectRequestWorking = false;
        },
      });
  }

  public closeRejectDialog(): void {
    this.rejectDialogVisible = false;
    this.rejectTarget = null;
  }

  public toggleRequestsPopover(event: Event): void {
    this.loadPendingRequests();
    this.pendingRequestsPopover?.toggle(event);
  }

  public getRequestTitle(appointment: IAppointment): string {
    const patient = appointment?.patient;
    if (patient?.name || patient?.surname) {
      return [patient.name, patient.surname].filter(Boolean).join(' ');
    }
    return this.translate.instant('APPOINTMENT.PATIENT.TITLE');
  }

  public getRequestTreatmentNames(appointment: IAppointment): string {
    return (appointment?.treatments || [])
      .map((row) => row?.treatment?.name)
      .filter(Boolean)
      .join(', ');
  }
}
